import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type { DocumentTemplate, DocumentTemplateId } from "../../constants/documentTemplates";
import {
  TEMPLATE_FORMS,
  type TemplateFormDef,
  type TemplateFormFieldDef,
} from "../../constants/templateForms";
import { useTemplateFormState, type TemplateFormState } from "../../hooks/useTemplateFormState";
import { openTemplateFormPdf } from "../../utils/openTemplateFormPdf";
import type { PdfLocale } from "../../utils/templateFormPdf";
import type { TemplateFormPrefill, TemplateFormValues } from "../../utils/templateForm";
import type { TemplateFormMembers } from "./PersonRowsField";
import type { SavedTemplateForm } from "../../hooks/useSavedTemplateForms";
import type { SavedFormsProjectChoice, SavedFormsState } from "./SavedFormPicker";
import { TemplateFormFooter, TemplateFormHeader, TemplateFormMetaRow } from "./TemplateFormChrome";
import { TemplateFormSection } from "./TemplateFormSection";

/** What a filled form hands back when the user saves it. */
export interface TemplateFormSubmission {
  templateId: DocumentTemplateId;
  documentNumber: string;
  documentName: string;
  /** True while the name is still the derived `<slug>-nr-<n>`. The save then
   *  rebuilds it from the number the database reserves — the on-form number is
   *  a client-side prediction, and at building scope a poor one (it is seeded
   *  from the page's building-wide list, not the destination project). */
  nameFollowsNumber: boolean;
  values: TemplateFormValues;
  attachmentsInPdf: boolean;
  signDigitally: boolean;
  /** The language the document printed in — stored so a reopened form still
   *  knows which rendering was filed. */
  pdfLocale: PdfLocale;
}

interface TemplateFormDialogProps {
  /** The template being filled in; `null` closes the form. */
  template: DocumentTemplate | null;
  onOpenChange: (open: boolean) => void;
  /** The back arrow — returns to the template picker. */
  onBack: () => void;
  prefill: TemplateFormPrefill;
  /** Names already used in this scope, for the auto-incremented number. */
  existingDocumentNames: string[];
  buildingId: string;
  /** The project the form files into; null when the building has none. */
  projectId: string | null;
  members: TemplateFormMembers;
  /** This template's already-filed forms, for "Choose pre-filled form". */
  savedForms: SavedFormsState;
  /** Lets the picker ask which project to draw filed forms from. Set only at
   *  building scope, where `projectId` is null and there is nothing to list
   *  until the user says. */
  savedFormsProject?: SavedFormsProjectChoice;
  /** Filed forms keyed by template, for the fields whose schema names one as
   *  an import source (the diary imports works from a covered-works act). */
  importSources: Partial<Record<DocumentTemplateId, SavedFormsState>>;
  onSave: (submission: TemplateFormSubmission) => void;
  /** True while the save is uploading and rendering. */
  saving: boolean;
  /** Whether this org and user can sign at all — when false the digital-signing
   *  toggle is disabled and says why, rather than promising something the
   *  organisation has not switched on. */
  canSign: boolean;
  /** Set when the form cannot be filed at all, whatever it contains — the
   *  building has no project to file into. Gates Save and says why. */
  saveUnavailableReason?: string;
  /** The "+ New document" button — focus returns to it when the form closes.
   *  Radix's own restore targets the picker's search input, which is already
   *  unmounted by then, so focus would fall back to `<body>`. */
  triggerRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * The shared template form. Every template renders through this one view —
 * the schema in `TEMPLATE_FORMS` decides the sections and fields, never the
 * layout, so a new template is a data change.
 */
export function TemplateFormDialog(props: TemplateFormDialogProps) {
  const template = props.template;
  const { t } = useLanguage();
  const dirtyRef = useRef(false);

  // Form state is component-local — Esc, a stray outside click, or the Back
  // arrow would drop every entered value, so a dirty form asks first.
  //
  // The ask is a real dialog rather than `window.confirm`, which meant a
  // browser-chrome box that ignores the app's styling and cannot say which
  // button is the destructive one. That makes it asynchronous: the close is
  // blocked, the intent is remembered, and it runs only once confirmed.
  // The intent is held in a ref as well as state: Radix's Action closes the
  // alert as part of the same click, which re-renders this component, and
  // reading the intent out of the render closure raced that. The ref is what
  // the confirm handler trusts; the state only drives whether it is open.
  const [pendingExit, setPendingExit] = useState<"back" | "close" | null>(null);
  const pendingExitRef = useRef<"back" | "close" | null>(null);

  const clearPendingExit = () => {
    pendingExitRef.current = null;
    setPendingExit(null);
  };

  // Set when the close is the Back arrow — the picker reopens and takes
  // focus, so restoring the trigger would fight its focus scope.
  const backRef = useRef(false);

  const exit = (intent: "back" | "close") => {
    if (intent === "back") {
      backRef.current = true;
      props.onBack();
    } else {
      props.onOpenChange(false);
    }
  };

  const requestExit = (intent: "back" | "close") => {
    if (!dirtyRef.current) {
      exit(intent);
      return;
    }
    pendingExitRef.current = intent;
    setPendingExit(intent);
  };

  // Esc and outside clicks: block Radix's own close and ask instead.
  const confirmDiscard = (event: { preventDefault: () => void }) => {
    if (!dirtyRef.current) return;
    event.preventDefault();
    // The discard dialog is itself "outside" this one, so opening it fires
    // this handler again. Without the guard that overwrote a pending Back with
    // a close, and confirming would dismiss the whole flow instead of
    // returning to the picker.
    if (pendingExitRef.current) return;
    pendingExitRef.current = "close";
    setPendingExit("close");
  };

  const handleBack = () => requestExit("back");
  // The header's own X. Radix's `DialogClose` would bypass the discard guard —
  // it closes the dialog rather than raising the events the guard listens to.
  const handleClose = () => requestExit("close");

  return (
    <Dialog open={template !== null} onOpenChange={props.onOpenChange}>
      <DialogContent
        hideCloseButton
        // A popup over the Documents page, not a fullscreen takeover: capped
        // at 85% of the viewport, with the section list scrolling inside it
        // (the header and footer stay put) however long the template is.
        className="flex max-h-[85dvh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0"
        // The header names the template and every field is labelled.
        aria-describedby={undefined}
        onEscapeKeyDown={confirmDiscard}
        // Deliberately NOT `onInteractOutside`, which is this plus focus-outside.
        //
        // Radix decides inside/outside by React tree, and the things this flow
        // opens over the form — the destination picker on Save, its project
        // Select, the discard ask itself — are siblings of it, so focus landing
        // in any of them read as the user walking away. Saving at building
        // scope opened the destination picker and was immediately asked to
        // discard everything entered.
        //
        // Focus-outside can never legitimately mean that here: the dialog is
        // modal and focus-trapped, so focus only leaves when something is
        // deliberately opened over it. Pointer-down-outside is the real signal,
        // and Radix suppresses it for any layer sitting beneath an open dialog
        // (`isPointerEventsEnabled` in DismissableLayer) — so its own layer
        // stack does this correctly, whatever the thing on top happens to be.
        onPointerDownOutside={confirmDiscard}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (backRef.current) {
            backRef.current = false;
            return;
          }
          props.triggerRef?.current?.focus();
        }}
      >
        {/* Keyed by template: picking another template starts a clean form. */}
        {template && (
          <TemplateForm
            key={template.id}
            template={template}
            dirtyRef={dirtyRef}
            onBack={handleBack}
            onClose={handleClose}
            closeLabel={t.common.close}
            prefill={props.prefill}
            existingDocumentNames={props.existingDocumentNames}
            buildingId={props.buildingId}
            projectId={props.projectId}
            members={props.members}
            savedForms={props.savedForms}
            savedFormsProject={props.savedFormsProject}
            importSources={props.importSources}
            onSave={props.onSave}
            saving={props.saving}
            canSign={props.canSign}
            saveUnavailableReason={props.saveUnavailableReason}
          />
        )}
      </DialogContent>

      <DiscardFormDialog
        copy={t.documents.templateForm.discard}
        open={pendingExit !== null}
        onCancel={clearPendingExit}
        onConfirm={() => {
          const intent = pendingExitRef.current;
          clearPendingExit();
          if (intent) exit(intent);
        }}
      />
    </Dialog>
  );
}

interface DiscardFormDialogProps {
  copy: TemplateFormTranslations["discard"];
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** The unsaved-changes ask. Cancel is the default action — the destructive
 *  button should never be the one a stray Enter lands on. */
function DiscardFormDialog({ copy, open, onCancel, onConfirm }: DiscardFormDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      {/* Sits above the form dialog and re-enables pointer events on itself:
          the form is a modal Radix dialog, which sets `pointer-events: none` on
          the body, and this alert portals to the body too — without these it
          renders perfectly and silently ignores every click. */}
      <AlertDialogContent className="pointer-events-auto z-[60]">
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{copy.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {copy.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type TemplateFormProps = Omit<
  TemplateFormDialogProps,
  "template" | "onOpenChange" | "triggerRef"
> & {
  template: DocumentTemplate;
  /** Mirrors the form's dirty flag up to the dialog's close guard. */
  dirtyRef: MutableRefObject<boolean>;
  /** Dismisses the flow, guarded by the same discard prompt as Esc. */
  onClose: () => void;
  closeLabel: string;
};

/** Why Save is unavailable, in the order the user would meet the problems. */
function saveBlockedReason(
  copy: TemplateFormTranslations,
  state: TemplateFormState,
  saveUnavailableReason?: string
): string | undefined {
  // Nothing the user types can clear this one, so it comes first.
  if (saveUnavailableReason) return saveUnavailableReason;
  // No `missingNumber` check: the number is read-only and always seeded, so
  // there is no way to empty it and nothing to refuse.
  if (!state.documentName.trim()) return copy.validation.missingName;
  if (state.missingFields.length === 0) return undefined;
  return copy.validation.missingFields.replace("{fields}", () =>
    state.missingFields.map((id) => copy.fields[id]).join(", ")
  );
}

function submissionOf(template: DocumentTemplate, state: TemplateFormState) {
  return {
    templateId: template.id,
    documentNumber: state.documentNumber,
    documentName: state.documentName,
    nameFollowsNumber: state.nameFollowsNumber,
    values: state.values,
    attachmentsInPdf: state.attachmentsInPdf,
    signDigitally: state.signDigitally,
    pdfLocale: state.pdfLocale,
  };
}

/** The PDF is generated in the browser from what is on screen, so it works
 *  before the document-forms backend exists — it just isn't stored yet. */
function openPreview(args: {
  form: TemplateFormDef;
  templateId: DocumentTemplateId;
  copy: TemplateFormTranslations;
  locale: PdfLocale;
  organisation: string;
  state: TemplateFormState;
}) {
  const { form, templateId, copy, locale, organisation, state } = args;
  openTemplateFormPdf(
    {
      form,
      templateId,
      locale,
      organisation,
      values: state.values,
      documentNumber: state.documentNumber,
      attachmentsInPdf: state.attachmentsInPdf,
      signDigitally: state.signDigitally,
    },
    `${state.documentName}.pdf`
  )
    .then(({ notAppended }) => {
      // Same honesty as the save path: the preview says which attachments the
      // PDF could only name, so the tick box is never taken on trust.
      if (notAppended.length > 0) {
        toast.warning(
          copy.saved.attachmentsNotEmbedded.replace("{names}", () => notAppended.join(", "))
        );
      }
    })
    .catch(() => toast.error(copy.pdf.failed));
}

interface TemplateFormBodyProps {
  copy: TemplateFormTranslations;
  form: TemplateFormDef;
  state: TemplateFormState;
  members: TemplateFormMembers;
  buildingId: string;
  /** The project the form files into; null when the building has none. */
  projectId: string | null;
  /** This template's own filed forms, for "Choose pre-filled form". */
  savedForms: SavedFormsState;
  /** Set only at building scope — see `SavedFormsProjectChoice`. */
  savedFormsProject?: SavedFormsProjectChoice;
  /** Filed forms by template, for the fields whose schema names a source. */
  importSources: Partial<Record<DocumentTemplateId, SavedFormsState>>;
  onPrefill: (form: SavedTemplateForm) => void;
  onImport: (field: TemplateFormFieldDef, form: SavedTemplateForm) => void;
}

/**
 * The scrolling middle of the popup — the number/name row and whatever
 * sections the template's schema declares. `min-h-0` is what makes it shrink
 * inside the capped-height dialog: a flex child's automatic minimum is its
 * content, which would grow the popup past 85dvh instead of scrolling.
 */
function TemplateFormBody({
  copy,
  form,
  state,
  members,
  buildingId,
  projectId,
  savedForms,
  savedFormsProject,
  importSources,
  onPrefill,
  onImport,
}: TemplateFormBodyProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
      <TemplateFormMetaRow
        copy={copy}
        documentNumber={state.documentNumber}
        documentName={state.documentName}
        onNameChange={state.changeName}
        savedForms={savedForms}
        savedFormsProject={savedFormsProject}
        onChoosePrefilled={onPrefill}
      />

      <div className="space-y-6">
        {form.sections.map((section) => (
          <TemplateFormSection
            key={section.id}
            section={section}
            copy={copy}
            values={state.values}
            members={members}
            buildingId={buildingId}
            projectId={projectId}
            onChange={state.setValue}
            importSources={importSources}
            savedFormsProject={savedFormsProject}
            onImport={onImport}
          />
        ))}
      </div>
    </div>
  );
}

// Props stay on one object: Codacy's analyser counts each destructured name
// as a parameter, and this view legitimately needs more than its limit of 8.
function TemplateForm(props: TemplateFormProps) {
  const { template } = props;
  const { t } = useLanguage();
  const copy = t.documents.templateForm;
  const form = TEMPLATE_FORMS[template.id];
  const state = useTemplateFormState(
    form,
    props.prefill,
    props.existingDocumentNames,
    template.traits.includes("digitalSigning")
  );

  const { dirtyRef } = props;
  useEffect(() => {
    dirtyRef.current = state.dirty;
    // A closed form has nothing left to lose — the guard resets with it.
    return () => {
      dirtyRef.current = false;
    };
  }, [dirtyRef, state.dirty]);

  const templateName = t.documents.newDocument.templates[template.id].name;
  const organisation = props.prefill.organisation;

  // Copies the answers across but keeps this form's own number and name — the
  // point is a filled-in starting point, not a second copy of that document.
  const handlePrefill = (source: SavedTemplateForm) => {
    state.loadValues(source.values);
    toast.info(copy.prefilledFrom.replace("{name}", () => source.documentName));
  };

  // Takes one field out of another form. An empty source field says so rather
  // than silently wiping what is already typed here.
  const handleImport = (field: TemplateFormFieldDef, source: SavedTemplateForm) => {
    const imported = field.importFrom ? source.values[field.importFrom.fieldId] : undefined;
    if (typeof imported !== "string" || imported.trim() === "") {
      toast.info(copy.importedNothing.replace("{name}", () => source.documentName));
      return;
    }
    state.setValue(field.id, imported);
    toast.success(
      copy.importedFrom
        .replace("{field}", () => copy.fields[field.id])
        .replace("{name}", () => source.documentName)
    );
  };

  return (
    <>
      <TemplateFormHeader
        copy={copy}
        templateName={templateName}
        onBack={props.onBack}
        onClose={props.onClose}
        closeLabel={props.closeLabel}
      />

      <TemplateFormBody
        copy={copy}
        form={form}
        state={state}
        members={props.members}
        buildingId={props.buildingId}
        projectId={props.projectId}
        savedForms={props.savedForms}
        savedFormsProject={props.savedFormsProject}
        importSources={props.importSources}
        onPrefill={handlePrefill}
        onImport={handleImport}
      />

      <TemplateFormFooter
        copy={copy}
        attachmentsInPdf={state.attachmentsInPdf}
        // A form nobody can sign is not "to be signed", whatever the template's
        // default says — otherwise Save would promise a flow that cannot run.
        signDigitally={props.canSign && state.signDigitally}
        onAttachmentsInPdfChange={state.setAttachmentsInPdf}
        onSignDigitallyChange={state.setSignDigitally}
        saveDisabled={!state.canSave || Boolean(props.saveUnavailableReason)}
        saveBlockedReason={saveBlockedReason(copy, state, props.saveUnavailableReason)}
        saving={props.saving}
        signingUnavailableReason={props.canSign ? undefined : copy.signing.unavailable}
        pdfLocale={state.pdfLocale}
        onPdfLocaleChange={state.setPdfLocale}
        onPreview={() =>
          openPreview({
            form,
            templateId: template.id,
            copy,
            locale: state.pdfLocale,
            organisation,
            state,
          })
        }
        onSave={() =>
          props.onSave({
            ...submissionOf(template, state),
            signDigitally: props.canSign && state.signDigitally,
          })
        }
      />
    </>
  );
}
